import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { cardDetailHref } from "../lib/cardLinks";
import {
  cadenceLabel,
  confirmUsage,
  creditSections,
  deadlineText,
  deleteUsage,
  fetchCredits,
  fetchPending,
  autoMarkPosted,
  AUTO_MARK_SOURCE,
  fetchUsage,
  monthDay,
  nextDayIso,
  nextDeadline,
  recordUsage,
  setActivation,
  setSnooze,
  snoozeChoices,
  statusIcon,
  urgencyDot,
  type ActiveCredit,
  type CreditsDto,
  type MarkUsedDefaults,
  type PendingUsage,
  type UsageRow,
} from "../lib/creditsPage";
import { Fold } from "../components/Fold";
import { Select } from "../components/Form";
import { Icon } from "../components/Icons";
import { PageTitle, SectionTitle } from "../components/Heading";
import { useBusyRows } from "../hooks/useBusyRows";
import { formatExact } from "../lib/money";

/**
 * Credits: the home page, a checklist of what is about to be lost.
 *
 * Built from the approved mockup (2026-09-10). Expiring leads only in a
 * credit's last week; otherwise To review (pending redemptions) leads. Every
 * credit is one row with a one-tap Used, and the chevron opens the full form and usage
 * history for the exceptions. Not-activated credits are left that way on
 * purpose, so they sit collapsed at the bottom instead of asking for review.
 */
export default function Credits() {
  const [data, setData] = useState<CreditsDto | null>(null);
  const [pending, setPending] = useState<PendingUsage[]>([]);
  const [error, setError] = useState<string | null>(null);
  // One open row at a time. The page used to mount a full form per credit,
  // ~84 live controls for a list whose usual action is a single tap.
  const [openId, setOpenId] = useState<number | null>(null);
  const [folds, setFolds] = useState<Record<string, boolean>>({});
  // `undo` reverses whatever the toast announces: a tap's redemption or a
  // snooze.
  const [toast, setToast] = useState<{
    text: string;
    undo: () => Promise<unknown>;
  } | null>(null);
  const rows = useBusyRows();
  const pendingRows = useBusyRows();

  const report = useCallback(
    (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
    [],
  );

  const load = useCallback(() => {
    // Used credits are fetched too and folded under their cadence, so one
    // marked by mistake stays a tap away from its history.
    Promise.all([fetchCredits({ hideUsed: false }), fetchPending()])
      .then(([d, p]) => {
        setData(d);
        setPending(p);
        setError(null);
      })
      .catch(report);
  }, [report]);

  useEffect(load, [load]);

  // The post-day sweep, once per visit and alongside the first load rather
  // than ahead of it, so the list is never held up by a write. A sweep that
  // wrote something reloads so the new pending rows show; one that fails
  // is not the page's error, since the list it found is still right.
  useEffect(() => {
    autoMarkPosted()
      .then((r) => {
        if (r.marked > 0) load();
      })
      .catch(() => undefined);
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  /**
   * Run a write, reload, and report whether it actually worked. The boolean
   * matters: ending at `.catch(setError)` RESOLVES, so a caller would read a
   * failed write as a success and clear the note the user typed.
   */
  const act = (p: Promise<unknown>): Promise<boolean> =>
    p
      .then(load)
      .then(() => true)
      .catch((e: unknown) => {
        report(e);
        return false;
      });

  /**
   * The one-tap Used: the server's prefill (what is left, dated on the
   * recurring post day or today), recorded as pending so it waits in To
   * review until it posts. Busy per row, because `record_usage` has no
   * idempotency key and a second tap would post a second redemption.
   */
  const markUsed = (c: ActiveCredit, prefill: MarkUsedDefaults) =>
    void rows.run(
      c.id,
      recordUsage(c.id, {
        amount_used: prefill.amount,
        date_used: prefill.date,
        period_start: c.period_start,
        period_end: c.period_end,
        status: "pending",
      })
        .then((usageId) => {
          setToast({
            text: `${c.name} marked used · ${formatExact(prefill.amount)}`,
            undo: () => deleteUsage(usageId),
          });
          load();
        })
        .catch(report),
    );

  if (error && !data) {
    return (
      <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
        {error}
      </div>
    );
  }
  if (!data) return <div className="text-sm text-neutral-400">Loading…</div>;

  const { expiring, groups } = creditSections(data.active);
  const next = nextDeadline(data.active);
  const toggleFold = (key: string) =>
    setFolds((f) => ({ ...f, [key]: !f[key] }));

  const row = (c: ActiveCredit) => {
    const prefill = data.mark_used_defaults[String(c.id)];
    const d = c.display;
    return (
      <CreditRow
        key={c.id}
        credit={c}
        deadline={deadlineText(c, data.today)}
        canUse={Boolean(prefill && !prefill.disabled)}
        busy={rows.isBusy(c.id)}
        open={openId === c.id}
        onUsed={() => prefill && markUsed(c, prefill)}
        onToggle={() => setOpenId((id) => (id === c.id ? null : c.id))}
      >
        <div className="text-xs text-neutral-400">
          {d.period_label} · {d.days_left}d left
          {d.category && <span> · {d.category}</span>}
        </div>
        {d.end_warning && (
          <div className="text-xs text-amber-400">⚠️ {d.end_warning}</div>
        )}
        {prefill && !prefill.disabled && (
          <MarkUsed
            // Keyed on the prefill, not just the credit: useState seeds
            // `amount` once per instance, so a partial redemption left the box
            // reading the old remaining. A changed key remounts and re-seeds.
            key={`${c.id}:${prefill.amount}:${prefill.date}`}
            prefill={prefill}
            onSubmit={(amount, date, notes, status) =>
              act(
                recordUsage(c.id, {
                  amount_used: amount,
                  date_used: date,
                  period_start: c.period_start,
                  period_end: c.period_end,
                  status,
                  notes: notes || null,
                }),
              )
            }
          />
        )}
        <SnoozePicker
          // Keyed on the period, so a picker left open re-seeds when it rolls.
          key={`snooze:${c.id}:${c.period_end}`}
          choices={snoozeChoices(c.period_end, data.today)}
          today={data.today}
          onSnooze={(until) =>
            act(setSnooze(c.id, until)).then((ok) => {
              if (ok) {
                setOpenId(null);
                setToast({
                  text: `${c.name} snoozed until ${monthDay(until, data.today)}`,
                  undo: () => setSnooze(c.id, null),
                });
              }
              return ok;
            })
          }
        />
        <UsageHistory credit={c} onChanged={load} onError={setError} />
      </CreditRow>
    );
  };

  const soonest = Math.min(...expiring.map((c) => c.display.days_left));
  const expiringLeft = expiring.reduce((sum, c) => sum + c.display.remaining, 0);

  return (
    <section className="space-y-7">
      <PageTitle>Credits</PageTitle>

      {error && (
        <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          {error}
        </div>
      )}

      {next && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="hero-num text-[42px]">
            {formatExact(next.remaining)}
          </span>
          <span className="text-sm text-neutral-400">
            left to use before {monthDay(next.date, data.today)}
          </span>
        </div>
      )}

      {expiring.length > 0 && (
        <section className="rounded-xl bg-red-950/25 px-3 pt-3 sm:px-4">
          <SectionHead
            title={
              soonest <= 0
                ? "Expiring today"
                : `Expiring in ${soonest} day${soonest === 1 ? "" : "s"}`
            }
            meta={`${formatExact(expiringLeft)} left`}
          />
          <ul>{expiring.map(row)}</ul>
        </section>
      )}

      {pending.length > 0 && (
        <section>
          <SectionHead title="To review" meta={`${pending.length} pending`} />
          <ul className="border-t border-neutral-800/70">
            {pending.map((u) => (
              <li
                key={u.id}
                className="flex min-h-[52px] items-center gap-2.5 border-b border-neutral-800/70 py-1.5 text-sm"
              >
                <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-sky-400" />
                <div className="min-w-0 grow">
                  <div className="truncate font-medium">{u.credit_name} · pending</div>
                  <div className="truncate text-xs text-neutral-500">
                    {u.card_name} ·{" "}
                    {u.source === AUTO_MARK_SOURCE
                      ? `usually posts ${monthDay(u.date_used, data.today)}`
                      : `marked ${monthDay(u.date_used, data.today)}`}
                    {u.notes ? ` · ${u.notes}` : ""}
                  </div>
                </div>
                <span className="shrink-0 tabular-nums">{formatExact(u.amount_used)}</span>
                <button
                  disabled={pendingRows.isBusy(u.id)}
                  onClick={() => void pendingRows.run(u.id, act(deleteUsage(u.id)))}
                  className="btn btn-ghost btn-sm shrink-0"
                >
                  Undo
                </button>
                <button
                  disabled={pendingRows.isBusy(u.id)}
                  onClick={() => void pendingRows.run(u.id, act(confirmUsage(u.id)))}
                  className="btn btn-sm shrink-0"
                >
                  Posted
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {groups.length > 0 && (
        <section>
          {expiring.length > 0 && <SectionHead title="Everything else" />}
          {groups.map((g) => {
            const usage = data.usage_by_cadence.find((u) => u.cadence === g.cadence);
            const foldKey = `used:${g.cadence}`;
            return (
              <div key={g.cadence}>
                <div className="flex items-baseline gap-3 pb-1 pt-4">
                  <h3 className="text-sm font-semibold">
                    {cadenceLabel(g.cadence, data.options.cadence_display)}
                  </h3>
                  {usage && (
                    <span className="ml-auto text-xs tabular-nums text-neutral-400">
                      {usage.used_count}/{usage.credit_count} used ·{" "}
                      {formatExact(usage.total_used)} of {formatExact(usage.total_available)}
                    </span>
                  )}
                </div>
                {usage && usage.total_available > 0 && (
                  <div className="meter mb-1">
                    <i style={{ width: `${Math.min(100, (usage.total_used / usage.total_available) * 100)}%` }} />
                  </div>
                )}
                <ul className="border-t border-neutral-800/70">
                  {g.open.map(row)}
                  {g.used.length > 0 && (
                    <li>
                      <Fold
                        open={Boolean(folds[foldKey])}
                        onClick={() => toggleFold(foldKey)}
                        label={`${g.used.length} used this period`}
                      />
                    </li>
                  )}
                  {folds[foldKey] && g.used.map(row)}
                </ul>
              </div>
            );
          })}
        </section>
      )}

      {data.is_empty && (
        <p className="text-sm text-neutral-400">
          No credits yet. Add them from a card on the Cards page.
        </p>
      )}

      {data.snoozed.length > 0 && (
        <section>
          <Fold
            open={Boolean(folds.snoozed)}
            onClick={() => toggleFold("snoozed")}
            label={`Snoozed (${data.snoozed.length})`}
          />
          {folds.snoozed && (
            <ul>
              {data.snoozed.map((c) => (
                <li
                  key={c.id}
                  className="flex min-h-[52px] items-center gap-2.5 border-b border-neutral-800/70 py-1.5 text-sm text-neutral-400"
                >
                  <div className="min-w-0 grow">
                    <div className="truncate">{c.name}</div>
                    <div className="truncate text-xs text-neutral-500">
                      <CreditCardLink id={c.card_id} name={c.card_name} /> · back {monthDay(c.snoozed_until, data.today)}
                    </div>
                  </div>
                  <button
                    onClick={() => void act(setSnooze(c.id, null))}
                    className="btn btn-ghost btn-sm shrink-0"
                  >
                    Unsnooze
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {data.inactive.length > 0 && (
        <section>
          <Fold
            open={Boolean(folds.inactive)}
            onClick={() => toggleFold("inactive")}
            label={`Not activated (${data.inactive.length})`}
          />
          {folds.inactive && (
            <ul>
              {data.inactive.map((c) => (
                <li
                  key={String(c.id)}
                  className="flex min-h-[52px] items-center gap-2.5 border-b border-neutral-800/70 py-1.5 text-sm text-neutral-400"
                >
                  <div className="min-w-0 grow">
                    <div className="truncate">{String(c.name)}</div>
                    <div className="truncate text-xs text-neutral-500">
                      <CreditCardLink id={c.card_id} name={String(c.card_name)} /> · {formatExact(Number(c.amount))}/
                      {cadenceLabel(String(c.cadence), data.options.cadence_display)}
                    </div>
                  </div>
                  <button
                    onClick={() => void act(setActivation(Number(c.id), true))}
                    className="btn btn-ghost btn-sm shrink-0"
                  >
                    Activate
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {data.discontinued.length > 0 && (
        <section>
          <Fold
            open={Boolean(folds.discontinued)}
            onClick={() => toggleFold("discontinued")}
            label={`Discontinued (${data.discontinued.length})`}
          />
          {folds.discontinued && (
            <ul>
              {data.discontinued.map((c) => (
                <li
                  key={String(c.id)}
                  className="space-y-1 border-b border-neutral-800/70 py-2.5 text-sm text-neutral-500"
                >
                  <div className="flex items-baseline gap-3">
                    <s className="grow">
                      <CreditCardLink id={c.card_id} name={String(c.card_name)} /> - {String(c.name)}
                    </s>
                    <span>
                      {formatExact(Number(c.amount))}/
                      {cadenceLabel(String(c.cadence), data.options.cadence_display)}
                    </span>
                  </div>
                  {/* The end date is the point: "ended" with no date says
                      nothing about whether the last period was claimed. */}
                  <p className="text-xs">
                    Ended {String(c.benefit_end_date)}. No longer refreshes.
                  </p>
                  {c.special_notes ? (
                    <p className="text-xs">{String(c.special_notes)}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex justify-center px-4 sm:bottom-6">
          <div
            role="status"
            className="pointer-events-auto flex items-center gap-4 rounded-xl bg-neutral-100 py-2 pr-2 pl-4 text-sm font-semibold text-neutral-900 shadow-lg"
          >
            {toast.text}
            <button
              onClick={() => {
                const { undo } = toast;
                setToast(null);
                void act(undo());
              }}
              className="btn btn-sm shrink-0 bg-neutral-950 text-neutral-100"
            >
              Undo
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Snooze inside an open row: quick dates or any later day. A snoozed credit
 * leaves the list, the cadence bar, Expiring and the podcast until that day,
 * and still counts toward the card's fee value.
 */
function SnoozePicker({
  choices,
  today,
  onSnooze,
}: {
  choices: { label: string; until: string }[];
  today: string;
  onSnooze: (until: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [until, setUntil] = useState(choices[0].until);
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn btn-ghost btn-sm inline-flex items-center gap-1.5 self-start"
      >
        <Icon name="clock" size={16} />
        Snooze
      </button>
    );
  }
  const valid = until > today;
  return (
    <div className="flex flex-col gap-2.5 rounded-xl bg-neutral-900 p-3">
      <div className="text-sm font-semibold">Snooze until</div>
      <div className="flex flex-wrap gap-2">
        {choices.map((ch) => (
          <button
            key={ch.until}
            type="button"
            aria-pressed={until === ch.until}
            onClick={() => setUntil(ch.until)}
            className={`btn btn-sm rounded-full ${
              until === ch.until ? "bg-sky-900 text-sky-50" : "btn-ghost"
            }`}
          >
            {ch.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-neutral-400">
        Or pick a date
        <input
          type="date"
          min={nextDayIso(today)}
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          className="min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 [color-scheme:dark]"
        />
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => {
            setBusy(true);
            void onSnooze(until).finally(() => setBusy(false));
          }}
          className="btn btn-sm"
        >
          {valid ? `Snooze until ${monthDay(until, today)}` : "Pick a later date"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost btn-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function SectionHead({ title, meta }: { title: string; meta?: string }) {
  return (
    <div className="mb-1 flex items-baseline gap-3">
      <SectionTitle>{title}</SectionTitle>
      {meta && (
        <span className="ml-auto text-xs tabular-nums text-neutral-400">{meta}</span>
      )}
    </div>
  );
}

/**
 * One credit, at rest: what is left, when it runs out, and a one-tap Used.
 * The chevron opens `children` (the full form and the usage history) under the row.
 */
function CreditRow({
  credit: c,
  deadline,
  canUse,
  busy,
  open,
  onUsed,
  onToggle,
  children,
}: {
  credit: ActiveCredit;
  deadline: string;
  canUse: boolean;
  busy: boolean;
  open: boolean;
  onUsed: () => void;
  onToggle: () => void;
  children: ReactNode;
}) {
  const d = c.display;
  const dot = urgencyDot(d.urgency);
  const used = d.remaining <= 0;
  return (
    <li className="border-b border-neutral-800/70 text-sm">
      <div className={`flex min-h-[52px] items-center gap-2.5 py-1.5 ${used ? "opacity-60" : ""}`}>
        <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${dot ?? ""}`} />
        <div className="min-w-0 grow">
          <div className="leading-snug font-semibold">{c.name}</div>
          <div className="mt-0.5 text-xs text-neutral-500"><CreditCardLink id={c.card_id} name={c.card_name} /></div>
        </div>
        {/* The deadline sits under the amount, not in its own column: real card
            names are long, and at 390px a fourth column cut every credit name
            to about 13 characters. (2026-09-10) */}
        <div className="shrink-0 text-right tabular-nums">
          {formatExact(used ? d.amount : d.remaining)}
          <div
            className={`text-[11px] ${
              d.period_urgent ? "text-red-400" : "text-neutral-500"
            }`}
          >
            {!used && d.remaining < d.amount ? `of ${formatExact(d.amount)} · ` : ""}
            {deadline}
          </div>
        </div>
        {canUse ? (
          <button
            onClick={onUsed}
            disabled={busy}
            aria-label={`Mark ${c.name} used`}
            className="btn btn-ghost btn-sm shrink-0"
          >
            <Icon name="check" size={15} strokeWidth={2.2} />
            Used
          </button>
        ) : used ? (
          <span className="w-[54px] shrink-0 text-center text-xs text-green-400">✓ Used</span>
        ) : (
          <span aria-hidden className="w-[54px] shrink-0" />
        )}
        <button
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`More for ${c.name}`}
          className="icon-btn -mr-2"
        >
          <Icon name="chevron" size={18} className="chev" />
        </button>
      </div>
      {open && (
        <div className="mb-3 space-y-3 rounded-xl bg-neutral-900 p-3">{children}</div>
      )}
    </li>
  );
}

/**
 * One credit's redemptions for the period it is currently in.
 *
 * Collapsed by default and fetched only when opened, matching the live page's
 * expander. Fetching all of them up front would be one request per credit on
 * every filter change, for a panel that is usually closed.
 *
 * This is the only place in the app a POSTED redemption can be undone. The
 * Pending Confirmation list at the top of the page shows pending rows only, so
 * without this a redemption entered as "posted" -- or confirmed by mistake --
 * was unreachable and the credit stayed spent for the rest of its period.
 */
function UsageHistory({
  credit,
  onChanged,
  onError,
}: {
  credit: ActiveCredit;
  onChanged: () => void;
  onError: (m: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<UsageRow[] | null>(null);
  const { isBusy, run } = useBusyRows();

  const report = useCallback(
    (e: unknown) => onError(e instanceof Error ? e.message : String(e)),
    [onError],
  );

  const load = useCallback(() => {
    fetchUsage(credit.id, credit.period_start, credit.period_end)
      .then(setRows)
      .catch(report);
  }, [credit.id, credit.period_start, credit.period_end, report]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="btn btn-ghost btn-sm"
      >
        Usage history
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-neutral-800 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-neutral-400">Usage history</span>
        <button
          onClick={() => setOpen(false)}
          className="btn btn-ghost btn-sm"
        >
          Hide
        </button>
      </div>

      {rows === null && <p className="text-xs text-neutral-500">Loading…</p>}
      {rows !== null && rows.length === 0 && (
        <p className="text-xs text-neutral-500">
          Nothing recorded this period.
        </p>
      )}

      {rows?.map((u) => (
        <div
          key={u.id}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
        >
          <span className="tabular-nums">{formatExact(u.amount_used)}</span>
          <span className="text-neutral-400">{u.date_used}</span>
          <span>
            {statusIcon(u.status)} {u.status}
          </span>
          <span className="grow text-neutral-500">{u.notes}</span>
          <button
            disabled={isBusy(u.id)}
            onClick={() => {
              // Confirm flips pending to posted; Undo DELETES the row and
              // gives the money back to the period. The server decides which
              // one this is, via `action`, rather than the browser re-reading
              // `status` and forming a second opinion.
              const write =
                u.action === "confirm" ? confirmUsage(u.id) : deleteUsage(u.id);
              void run(
                u.id,
                write
                  .then(() => {
                    load();
                    onChanged();
                  })
                  .catch(report),
              );
            }}
            className="btn btn-ghost btn-sm"
          >
            {u.action === "confirm" ? "Confirm" : "Undo"}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * The Mark Used row.
 *
 * `onSubmit` returns a promise and this form waits on it, which is not
 * decoration. An always-enabled button plus a POST followed by two GETs is a
 * double redemption one impatient tap apart, and `record_usage` takes no
 * idempotency key -- the second tap posts a second row.
 */
function MarkUsed({
  prefill,
  onSubmit,
}: {
  prefill: { amount: number; max_amount: number; date: string };
  onSubmit: (
    amount: number,
    date: string,
    notes: string,
    status: string,
  ) => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(String(prefill.amount));
  const [date, setDate] = useState(prefill.date);
  const [notes, setNotes] = useState("");
  // "pending" first, matching the live page's selectbox, which defaults to it.
  // A redemption is not confirmed until it shows up on a statement, and the
  // Pending Confirmation section at the top of this page is where it waits.
  // This form used to send no status at all, so the server default decided --
  // and that default was "posted", which walked every entry straight past the
  // review step this same page renders.
  const [status, setStatus] = useState("pending");

  return (
    <form
      className="grid grid-cols-2 items-end gap-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        // The note is cleared on success only. Clearing it synchronously threw
        // away what the user typed whenever the write failed, so the retry had
        // to be typed again from memory.
        void onSubmit(Number(amount), date, notes, status)
          .then((ok) => {
            if (ok) setNotes("");
          })
          .finally(() => setBusy(false));
      }}
    >
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Amount
        <input
          type="number"
          step="0.01"
          max={prefill.max_amount}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="min-h-10 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Date
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="min-h-10 w-full min-w-0 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        />
      </label>
      <Select
        label="Status"
        value={status}
        options={["pending", "posted"]}
        onChange={setStatus}
      />
      <label className="flex min-w-0 flex-col gap-1 text-xs text-neutral-400">
        Notes
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="min-h-10 w-full min-w-0 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        className="btn btn-primary col-span-2 justify-self-start"
      >
        {busy ? "Marking…" : "Mark Used"}
      </button>
    </form>
  );
}

/** Older API payloads remain readable without guessing a card from its name. */
function CreditCardLink({ id, name }: { id: unknown; name: string }) {
  if (typeof id !== "number" || !Number.isSafeInteger(id) || id <= 0) return <>{name}</>;
  return <Link to={cardDetailHref(id)} className="underline decoration-neutral-700 underline-offset-2 hover:text-accent">{name}</Link>;
}
